'use client';

import { useCallback, useEffect, useState, type RefObject } from 'react';
import CmsImage from '@/components/ui/CmsImage';
import { localizeUrl, stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiMediaUrl } from '@/lib/strapi-media-url';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import type { PncImageGridSlider, PncModalContent } from '@/types/people-and-culture';
import { formatPncStatValue } from '../lib/format-pnc-stat';
import WonderShaktiModal from './WonderShaktiModal';

interface InitiativesSectionProps {
  locale: string;
  sectionRef: RefObject<HTMLDivElement>;
  zone: PncImageGridSlider;
}

function InitiativeImageColumn({
  activeInitiative,
  initiativeHeading,
  mainImage,
  thumbImages,
  overlayTag,
  overlaySubtitle,
}: {
  activeInitiative: number;
  initiativeHeading: string;
  mainImage: string;
  thumbImages: string[];
  overlayTag: string;
  overlaySubtitle: string;
}) {
  return (
    <div data-init-left>
      <div key={`init-images-${activeInitiative}`} className="initiative-enter">
        <div className="relative h-56 w-full overflow-hidden rounded-2xl sm:h-64 md:h-72 lg:h-80">
          <CmsImage
            src={mainImage || null}
            alt={initiativeHeading}
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            className="object-cover"
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
            {overlayTag ? (
              <span className="inline-block rounded-full bg-[#ed1c22] px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white sm:text-[11px]">
                {overlayTag}
              </span>
            ) : null}
            {overlaySubtitle ? (
              <p className="mt-2 text-sm font-bold text-white">{overlaySubtitle}</p>
            ) : null}
          </div>
        </div>
        {thumbImages.length > 0 ? (
          <div className="mt-3 grid grid-cols-2 gap-3">
            {thumbImages.map((src, i) => (
              <div key={src} className="relative h-28 overflow-hidden rounded-xl sm:h-32 md:h-36">
                <CmsImage
                  src={src || null}
                  alt={`${initiativeHeading} ${i + 1}`}
                  fill
                  sizes="(max-width: 1024px) 50vw, 25vw"
                  className="object-cover"
                  fallbackSrc={MEDIA_PLACEHOLDER}
                />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function InitiativeStatsGrid({
  stats,
}: {
  stats: NonNullable<PncImageGridSlider['slideItem']>[number]['stats'];
}) {
  if (!stats || stats.length === 0) return null;
  return (
    <div className="mb-6 grid grid-cols-2 gap-3">
      {stats.map((stat) => (
        <div
          key={stat.id}
          data-stat-box
          className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-md sm:px-5 sm:py-4"
        >
          <p className="text-xl font-bold text-[#ed1c22] sm:text-2xl">{formatPncStatValue(stat)}</p>
          <p className="mt-0.5 text-xs text-gray-400">{stat.statheading}</p>
        </div>
      ))}
    </div>
  );
}

function InitiativeContentColumn({
  activeInitiative,
  heading,
  descriptionHtml,
  stats,
  locationText,
  buttonLabel,
  showButton,
  buttonAriaLabel,
  buttonActionProps,
}: {
  activeInitiative: number;
  heading: string;
  descriptionHtml: string;
  stats: NonNullable<PncImageGridSlider['slideItem']>[number]['stats'];
  locationText: string;
  buttonLabel: string | undefined;
  showButton: boolean;
  buttonAriaLabel: string;
  buttonActionProps:
    | { onClick: () => void }
    | ({ href: string } & ReturnType<typeof getCmsButtonLinkProps>);
}) {
  return (
    <div data-init-right className="flex flex-col justify-center">
      <div key={`init-content-${activeInitiative}`} className="initiative-enter">
        <h3 className="mb-3 text-3xl font-bold text-[#0a0a0a] sm:mb-4 sm:text-4xl md:text-5xl">
          {heading}
        </h3>
        <p className="mb-6 text-sm leading-relaxed text-gray-500 sm:mb-8">{descriptionHtml}</p>

        <InitiativeStatsGrid stats={stats} />

        {locationText ? (
          <div className="mb-6 flex items-center gap-2 text-sm text-gray-400">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="h-4 w-4 shrink-0"
            >
              <circle cx="12" cy="10" r="3" />
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
            </svg>
            <span>{locationText}</span>
          </div>
        ) : null}

        {buttonLabel && showButton ? (
          <SwapTextButton
            text={buttonLabel}
            hoverText={buttonLabel}
            aria-label={buttonAriaLabel}
            {...buttonActionProps}
            className="!rounded-full !px-7 !py-3 shadow-[0_4px_20px_rgba(237,28,34,0.35)]"
          />
        ) : null}
      </div>
    </div>
  );
}

export default function InitiativesSection({ locale, sectionRef, zone }: InitiativesSectionProps) {
  const slides = zone.slideItem ?? [];
  const [activeInitiative, setActiveInitiative] = useState(0);
  const [showShaktiModal, setShowShaktiModal] = useState(false);
  const [modalContent, setModalContent] = useState<PncModalContent | null>(null);

  const initiativeCount = slides.length;

  const initiative = slides[activeInitiative];
  const imageGrid = initiative?.imageGrid ?? [];
  const stats = initiative?.stats ?? [];
  const locationText = initiative?.locationText ?? '';
  const mainImage = getStrapiMediaUrl(imageGrid[0]?.media, 'large') ?? '';
  const thumbImages = imageGrid
    .slice(1, 3)
    .map((item) => getStrapiMediaUrl(item.media, 'medium') ?? '');
  const overlayTag = imageGrid[0]?.heading?.trim() || '';
  const overlaySubtitle = imageGrid[0]?.description ? stripHtml(imageGrid[0].description) : '';
  const button = initiative?.button?.[0];
  const hasModal = Boolean(initiative?.modalContent);
  const buttonLabel = button?.buttonText?.trim();
  const buttonHref =
    button?.buttonUrl && !button.buttonUrl.startsWith('#')
      ? localizeUrl(button.buttonUrl, locale)
      : null;

  useEffect(() => {
    setShowShaktiModal(false);
    setModalContent(null);
  }, [activeInitiative]);

  const goInitiative = useCallback(
    (dir: 1 | -1) => {
      if (initiativeCount <= 1) return;
      setActiveInitiative((i) => (i + dir + initiativeCount) % initiativeCount);
    },
    [initiativeCount],
  );

  const openModal = () => {
    if (initiative?.modalContent) {
      setModalContent(initiative.modalContent);
      setShowShaktiModal(true);
    }
  };

  // Must be after hooks (above) so `useEffect`/`useCallback` are never called conditionally.
  if (initiativeCount === 0) return null;

  const buttonActionProps = hasModal
    ? { onClick: openModal }
    : { href: buttonHref!, ...getCmsButtonLinkProps(button) };

  return (
    <>
      <section ref={sectionRef} className="bg-white px-4 sm:px-6 md:px-16 lg:px-24">
        <div
          data-reveal
          className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-start sm:justify-between"
        >
          <div>
            <h2 className="text-3xl font-bold text-[#0a0a0a] sm:text-4xl md:text-5xl">
              {zone.heading}
            </h2>
          </div>
          {initiativeCount > 1 ? (
            <div className="flex shrink-0 items-center gap-3">
              <span className="text-sm font-semibold text-gray-400">
                <span className="text-[#0a0a0a]">
                  {String(activeInitiative + 1).padStart(2, '0')}
                </span>{' '}
                / {String(initiativeCount).padStart(2, '0')}
              </span>
              <button
                type="button"
                aria-label="Previous campaign"
                onClick={() => goInitiative(-1)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition hover:border-gray-500 hover:text-gray-800"
              >
                ←
              </button>
              <button
                type="button"
                aria-label="Next campaign"
                onClick={() => goInitiative(1)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ed1c22] text-white transition hover:bg-[#c8151b]"
              >
                →
              </button>
            </div>
          ) : null}
        </div>

        <div data-reveal className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <InitiativeImageColumn
            activeInitiative={activeInitiative}
            initiativeHeading={initiative?.heading ?? ''}
            mainImage={mainImage}
            thumbImages={thumbImages}
            overlayTag={overlayTag}
            overlaySubtitle={overlaySubtitle}
          />

          <InitiativeContentColumn
            activeInitiative={activeInitiative}
            heading={initiative?.heading ?? ''}
            descriptionHtml={initiative?.description ? stripHtml(initiative.description) : ''}
            stats={stats}
            locationText={locationText}
            buttonLabel={buttonLabel}
            showButton={hasModal || Boolean(buttonHref)}
            buttonAriaLabel={button?.ariaLabel?.trim() || buttonLabel || ''}
            buttonActionProps={buttonActionProps}
          />
        </div>

        {initiativeCount > 1 ? (
          <div className="mt-6 flex justify-center gap-2 sm:mt-8">
            {slides.map((item, i) => (
              <button
                key={item.id}
                type="button"
                aria-label={`Show ${item.heading}`}
                aria-current={i === activeInitiative ? 'true' : undefined}
                onClick={() => setActiveInitiative(i)}
                className={`block rounded-full transition-all duration-300 ${
                  i === activeInitiative
                    ? 'h-2 w-6 bg-[#ed1c22]'
                    : 'h-2 w-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        ) : null}
      </section>

      <WonderShaktiModal
        isOpen={showShaktiModal}
        onClose={() => setShowShaktiModal(false)}
        content={modalContent}
        locationText={locationText}
      />
    </>
  );
}
